import Service, { type Registry as Services, service } from '@ember/service';
import { loadSpace } from '@usersnap/browser';
import type { User } from 'frontend-utils/models';
import ENV from 'lumen/config/environment';

export default class UsersnapService extends Service {
  @service declare access: Services['access'];
  @service declare errorTracking: Services['error-tracking'];

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  isUsersnapEnabled = ENV.usersnap.enabled;
  // `loadSpaceFunction` is added, so we can overwrite it in tests in order to avoid calling the real Usersnap API.
  // `sinon.stub` does not work, because of error:
  // `TypeError: Descriptor for property loadSpace is non-configurable and non-writable`
  // Another solution would be to export `loadSpace` as our own module (that would allow us to stub it), but I prefer this simpler solution.
  loadSpaceFunction = loadSpace;
  usersnapAPI: Awaited<ReturnType<typeof loadSpace>> | null = null;

  async initialise(user: User): Promise<void> {
    const { isUsersnapEnabled } = this;

    if (!isUsersnapEnabled || !user) {
      return;
    }

    // Don't initialise multiple times
    if (this.usersnapAPI) {
      return;
    }

    try {
      let benchmarkInsightSubscriptionStatus: string =
        this.hotelAndMarketService.hotel?.productState?.benchmark_insight
          ?.subscription_status ?? '';

      if (this.hotelAndMarketService.hotel?.benchmarkInsightIsLight) {
        benchmarkInsightSubscriptionStatus = 'light';
      }

      const api = await this.loadSpaceFunction(ENV.usersnap.spaceAPIKey);

      await api.init({
        user: {
          email: user.email,
          // @ts-expect-error: Type 'string | null' is not assignable to type 'string | undefined'.
          userId: user.id,
          benchmarkInsightSubscriptionStatus,
          dataReplatforming: this.access.hasDataReplatforming,
        },
        locale: user.locale,
      });

      this.usersnapAPI = api;
    } catch (error) {
      // This doesn't catch the API error if the request fails, but it might catch other errors.
      this.errorTracking.captureException(error);
    }
  }

  logEvent(eventName: string): void {
    if (!this.usersnapAPI) {
      return;
    }

    try {
      void this.usersnapAPI.logEvent(eventName);
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }

  async terminate(): Promise<void> {
    if (!this.usersnapAPI) {
      return;
    }

    // We've noticed this destroy function sometimes throws an error, so wrapped it in try/catch for now
    try {
      await this.usersnapAPI.destroy();
      this.usersnapAPI = null;
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    usersnap: UsersnapService;
  }
}
